import ApiPage from '@/apps/pages/ApiPage'
import AppsPage from '@/apps/pages/AppsPage'
import CirclesPage from '@/circle/pages/CirclesPage'
import Loading from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import Page404 from '@/common/pages/Page404'
import DashboardPage from '@/dashboard/pages/DashboardPage'
import DecisionPage from '@/decision/pages/DecisionPage '
import SettingsLayout from '@/layout/components/SettingsLayout'
import MeetingPage from '@/meeting/pages/MeetingPage'
import MeetingRecurringPage from '@/meeting/pages/MeetingRecurringPage'
import { useSubscribeCurrentMeeting } from '@/member/hooks/useSubscribeCurrentMeeting'
import MembersPage from '@/member/pages/MembersPage'
import OrgSetupTrigger from '@/onboarding/components/OrgSetupTrigger'
import NavigateInOrg from '@/org/components/NavigateInOrg'
import { useOrgContext } from '@/org/contexts/OrgContext'
import useOrgLifecycleTracking from '@/org/hooks/useOrgLifecycleTracking'
import ExportPage from '@/org/pages/ExportPage'
import OrgSettingsPage from '@/org/pages/OrgSettingsPage'
import TaskPage from '@/task/pages/TaskPage'
import TasksPage from '@/task/pages/TasksPage'
import NextStepsPage from '@/nextStep/pages/NextStepsPage'
import ThreadPage from '@/thread/pages/ThreadPage'
import ThreadsPage from '@/thread/pages/ThreadsPage'
import CredentialsSettingsPage from '@/user/pages/CredentialsSettingsPage'
import NotificationsSettingsPage from '@/user/pages/NotificationsSettingsPage'
import React, { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import WhatsNewPage from '@/release/pages/WhatsNewPage'

// Lazy pages
const MeetingsPage = lazy(() => import('@/meeting/pages/MeetingsPage'))
const SubscriptionPage = lazy(
  () => import('@/orgSubscription/pages/SubscriptionPage')
)
const CircleExportPage = lazy(() => import('@/circle/pages/CircleExportPage'))

// Renders the org pages once the org data is provided by DbOrgProvider.
export default function OrgRouteContent() {
  const { org, loading, error } = useOrgContext()

  // Update current meeting in store
  useSubscribeCurrentMeeting()

  // Plan, org type and size, attached to the analytics session

  // Return and activation events of young orgs
  useOrgLifecycleTracking()

  return (
    <Suspense fallback={<Loading active center />}>
      <Loading center active={loading} />
      {error && <TextError error={error} />}

      {!org && !loading ? (
        <Page404 />
      ) : (
        <>
          <OrgSetupTrigger />
          <Routes>
            <Route index element={<Navigate to="news" replace />} />
            <Route path="roles" element={<CirclesPage />} />
            <Route path="news" element={<DashboardPage />} />
            <Route path="threads/:threadId" element={<ThreadPage />} />
            <Route path="threads" element={<ThreadsPage />} />
            <Route path="meetings/:meetingId" element={<MeetingPage />} />
            <Route
              path="meetings-recurring/:id"
              element={<MeetingRecurringPage />}
            />
            <Route path="meetings" element={<MeetingsPage />} />
            <Route path="tasks/:taskId" element={<TaskPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="next-steps" element={<NextStepsPage />} />
            <Route path="whats-new" element={<WhatsNewPage />} />
            <Route path="decisions/:decisionId" element={<DecisionPage />} />
            <Route path="export-circle" element={<CircleExportPage />} />
            <Route path="settings" element={<SettingsLayout />}>
              <Route path="members" element={<MembersPage />} />
              <Route path="org" element={<OrgSettingsPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="apps" element={<AppsPage />} />
              <Route path="api-keys" element={<ApiPage />} />
              <Route path="export" element={<ExportPage />} />
              <Route path="credentials" element={<CredentialsSettingsPage />} />
              <Route
                path="notifications"
                element={<NotificationsSettingsPage />}
              />
            </Route>

            {/* Pages moved to settings */}
            <Route
              path="members"
              element={<NavigateInOrg to="settings/members" />}
            />
            <Route
              path="subscription"
              element={<NavigateInOrg to="settings/subscription" />}
            />

            {/* History is now a panel of the org chart page */}
            <Route
              path="logs"
              element={<NavigateInOrg to="roles" search="?panel=logs" />}
            />

            <Route path="*" element={<Page404 />} />
          </Routes>
        </>
      )}
    </Suspense>
  )
}
